import { logDocumentCamera, warnDocumentCamera } from "./documentCameraDebug";
import { DOCUMENT_CONFIG } from "./documentDetectionConfig";
import { createDocumentDetectionWorker } from "./DocumentDetectionWorkerFactory";

const INITIALIZATION_TIMEOUT_MS = DOCUMENT_CONFIG.initializationTimeout;
const DETECTION_TIMEOUT_MS = DOCUMENT_CONFIG.detectionTimeout;

const createClientError = (message) => {
  const error = new Error(message);
  error.name = "DocumentDetectionError";
  return error;
};

/**
 * 主线程侧的检测 Worker 客户端。
 *
 * 职责：只初始化一个共享 Worker、为每帧分配 requestId、匹配异步响应，
 * 并在初始化/单帧检测超时时拒绝 Promise，避免相机流程永久等待。
 */
export class DocumentDetectionClient {
  constructor({ workerFactory = createDocumentDetectionWorker } = {}) {
    this.workerFactory = workerFactory;
    this.worker = null;
    this.ready = false;
    this.initializationPromise = null;
    this.initializationHandlers = null;
    this.pendingRequests = new Map();
    this.nextRequestId = 1;
  }

  stopWorker() {
    this.worker?.removeEventListener("message", this.handleMessage);
    this.worker?.removeEventListener("error", this.handleWorkerError);
    this.worker?.terminate();
    this.worker = null;
    this.ready = false;
  }

  initialize() {
    if (this.ready) {
      return Promise.resolve();
    }
    if (this.initializationPromise) {
      // 多个页面/调用方同时预加载时复用同一个 Promise，不重复创建 WASM 扫描器。
      logDocumentCamera("Waiting for detector worker initialization");
      return this.initializationPromise;
    }

    const startedAt = performance.now();
    logDocumentCamera("Starting Scanic detection worker");
    try {
      this.worker = this.workerFactory();
    } catch (workerError) {
      const error = createClientError(
        workerError?.message || "Document detection worker could not start.",
      );
      warnDocumentCamera("Detector worker initialization failed", error);
      return Promise.reject(error);
    }
    this.worker.addEventListener("message", this.handleMessage);
    this.worker.addEventListener("error", this.handleWorkerError);

    this.initializationPromise = new Promise((resolve, reject) => {
      const rejectInitialization = (error) => {
        globalThis.clearTimeout(timeoutId);
        this.initializationPromise = null;
        this.initializationHandlers = null;
        this.stopWorker();
        warnDocumentCamera("Detector worker initialization failed", error);
        reject(error);
      };
      const timeoutId = globalThis.setTimeout(() => {
        const error = createClientError(
          "Document detection initialization timed out.",
        );
        warnDocumentCamera("Detector worker initialization timed out", error);
        rejectInitialization(error);
      }, INITIALIZATION_TIMEOUT_MS);

      this.initializationHandlers = {
        resolve: () => {
          globalThis.clearTimeout(timeoutId);
          this.ready = true;
          this.initializationHandlers = null;
          logDocumentCamera("Scanic detector worker ready", {
            initializationMs: performance.now() - startedAt,
          });
          resolve();
        },
        reject: rejectInitialization,
      };
    });

    this.worker.postMessage({ type: "initialize" });
    return this.initializationPromise;
  }

  detect = async (imageData) => {
    await this.initialize();

    const requestId = this.nextRequestId;
    this.nextRequestId += 1;

    return new Promise((resolve, reject) => {
      const timeoutId = globalThis.setTimeout(() => {
        this.pendingRequests.delete(requestId);
        const error = createClientError("Document detection timed out.");
        warnDocumentCamera(`第 ${requestId} 帧检测超时`, error);
        reject(error);
      }, DETECTION_TIMEOUT_MS);

      this.pendingRequests.set(requestId, {
        resolve,
        reject,
        timeoutId,
      });
      // 转移 ImageData 的 ArrayBuffer 所有权，避免向 Worker 复制整帧像素。
      this.worker.postMessage({ type: "detect", requestId, imageData }, [
        imageData.data.buffer,
      ]);
    });
  };

  handleMessage = ({ data }) => {
    if (data?.type === "ready") {
      this.initializationHandlers?.resolve();
      return;
    }

    if (data?.type === "error" && !data.requestId) {
      this.initializationHandlers?.reject(createClientError(data.message));
      return;
    }

    // requestId 将乱序返回的 Worker 结果交还给对应 detect() Promise。
    const pendingRequest = this.pendingRequests.get(data?.requestId);
    if (!pendingRequest) return;

    globalThis.clearTimeout(pendingRequest.timeoutId);
    this.pendingRequests.delete(data.requestId);

    if (data.type === "result") {
      pendingRequest.resolve({
        detectorOutput: data.detectorOutput,
        frameWidth: data.frameWidth,
        frameHeight: data.frameHeight,
        detectorTimeMs: data.detectorTimeMs,
      });
    } else {
      pendingRequest.reject(createClientError(data.message));
    }
  };

  handleWorkerError = (workerError) => {
    const error = createClientError("Document detection worker failed.");
    warnDocumentCamera("Detection worker crashed", workerError || error);
    this.initializationHandlers?.reject(error);
    this.initializationHandlers = null;

    this.pendingRequests.forEach(({ reject, timeoutId }) => {
      globalThis.clearTimeout(timeoutId);
      reject(error);
    });
    this.pendingRequests.clear();
    this.stopWorker();
    this.initializationPromise = null;
    this.initializationHandlers = null;
  };

  dispose() {
    logDocumentCamera("Stopping detector worker");
    this.stopWorker();
    this.initializationPromise = null;
    this.initializationHandlers = null;

    const error = createClientError("Document detection was stopped.");
    this.pendingRequests.forEach(({ reject, timeoutId }) => {
      globalThis.clearTimeout(timeoutId);
      reject(error);
    });
    this.pendingRequests.clear();
  }
}

let sharedClient = null;

// preparation 页面和拍摄页面共享实例，使提前加载的检测器可直接复用。
export const getDocumentDetectionClient = () => {
  if (!sharedClient) sharedClient = new DocumentDetectionClient();
  return sharedClient;
};

export const preloadDocumentDetector = () =>
  getDocumentDetectionClient().initialize();
